import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../../Providers/AuthProvider";
import { useOffers } from "../../Providers/OffersProvider";
import ScrollUpArrow from "../../Components/Components/ScrollUpArrow";
import useCheckUserRole from "../../Components/BasketPage/Hooks/useCheckUserRole.ts";
import NoAccess from "../../Components/NoAccess.jsx";
import { UserRole } from "../../Data/UserRolesEnum.ts";
import ErrorBoundary from "../../Components/ErrorBoundary/ErrorBoundary.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import useCheckLicense from "../../Hooks/useCheckLicense.ts";
import { Licenses } from "../../Data/Licenses.ts";
import LoadingSpinner from "../../Components/Components/LoadingSpinner.jsx";
import YesNoMessageModal from "../../Components/Components/Modals/YesNoMessageModal.jsx";
import { toast } from "react-toastify";
import useDownloadCustomerOffer from "../../Hooks/useDownloadCustomerOffer.jsx";
import YesNoCancelMessageModal from "../../Components/Components/Modals/YesNoCancelMessageModal.jsx";
import OffersTable from "./Components/OffersPage/OffersTable.jsx";
import axios from "axios";
import { AccountCurrentView, OfferCurrentView } from "../../Data/PageModeEnums.ts";
import { useParams } from "../../Providers/ParamsProvider.jsx";
import MobileAccountNavBar from "../../Components/MobileComponents/NavBarMobile.jsx/MobileAccountNavBar.jsx";

const OffersPageIndex = () => {
    const retailerOffersObserver = useRef()
    const requestOffersObserver = useRef()
    const customerOffersObserver = useRef()
    const { checkLicense } = useCheckLicense()
    const [isValidLicense, setIsValidLicense] = useState(checkLicense([Licenses.TgInstalacje]))
    const navigate = useNavigate()
    const { authState } = useAuth()
    const { isMobile } = useParams()
    const { customerOffers, retailerOffers, requestOffers, isLoadingCustomerOffers, isLoadingRetailerOffers, isLoadingRequestOffers, handleGetCustomerOffers, handleGetRequestOffers,
        handleGetRetailerOffers, handleCreateUpdateSavedOffer, handleDeleteSavedOffer, handleDeleteRetailerOffer, retailerOffersHasMore, setRetailerOffersPage, retailerOffersPage,
        requestOffersHasMore, setRequestOffersPage, requestOffersPage, customerOffersHasMore, setCustomerOffersPage, customerOffersPage, handleGetNumberOfUnreadCustomerOffer,
        handleChangeOfferStatus
    } = useOffers()

    const [currentUser, setCurrentUser] = useState(authState.user ? authState.user : {})
    const location = useLocation()
    const { params } = location.state || {}

    const setInitialOpenTab = () => {
        let selectedTab = params?.selectedTab
        if (selectedTab === 'Zapytania') {
            return OfferCurrentView.OfferRequest
        }
        else if (selectedTab === 'Moje') {
            return OfferCurrentView.OfferRetailer
        }
        else {
            return OfferCurrentView.OfferCustomer
        }
    }

    const [currentView, setCurrentView] = useState(setInitialOpenTab())
    const { isUserHasAccess } = useCheckUserRole()
    const [isLoadingOfferRequest, setIsLoadingOfferRequest] = useState(false)
    const [showCreateOfferRequestModal, setShowCreateOfferRequestModal] = useState(false)
    const [itemsToCreateOfferRequest, setItemsToCreateOfferRequest] = useState(null)
    const [showCreateRetailerOfferModal, setShowCreateRetailerOfferModal] = useState(false)
    const [itemsToCreateRetailerOffer, setItemsToCreateRetailerOffer] = useState(null)
    const [offerExpiredDate, setOfferExpiredDate] = useState(null)

    const [itemsToEditOffer, setItemsToEditOffer] = useState([])
    const [offerNo, setOfferNo] = useState(null)
    const [showEditOfferModal, setShowEditOfferModal] = useState(false)

    const [loadingText, setLoadingText] = useState('')
    const { downloadCustomerOffer, handleDownloadRetailerOfferPdf, downloadingOfferNo } = useDownloadCustomerOffer()
    const [downloadOfferPdfModalObj, setDownloadOfferPdfModalObj] = useState({ show: false, offerNo: -1, offerSiteId: -1 })
    const [isLoadingBasket, setIsLoadingBasket] = useState(false)

    const [offersFilter, setOffersFilter] = useState({
        custId: authState.custId,
        siteId: '',
        dateFrom: null,
        dateTo: null,
        invest: '',
        orderBy: 1,
    })

    const lastBookElementRefRetailerOffers = useCallback(node => {
        if (isLoadingRetailerOffers) {
            return
        }
        if (retailerOffersObserver.current) {
            retailerOffersObserver.current.disconnect()
        }
        retailerOffersObserver.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && retailerOffersHasMore) {
                setRetailerOffersPage(prevPageNumber => prevPageNumber + 1)
            }
        })
        if (node) retailerOffersObserver.current.observe(node)
    }, [isLoadingRetailerOffers, retailerOffersPage])

    const lastBookElementRefRequestOffers = useCallback(node => {
        if (isLoadingRequestOffers) {
            return
        }
        if (requestOffersObserver.current) {
            requestOffersObserver.current.disconnect()
        }
        requestOffersObserver.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && requestOffersHasMore) {
                setRequestOffersPage(prevPageNumber => prevPageNumber + 1)
            }
        })
        if (node) requestOffersObserver.current.observe(node)
    }, [isLoadingRequestOffers, requestOffersPage])

    const lastBookElementRefCustomerOffers = useCallback(node => {
        if (isLoadingCustomerOffers) {
            return
        }
        if (customerOffersObserver.current) {
            customerOffersObserver.current.disconnect()
        }
        customerOffersObserver.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && customerOffersHasMore) {
                setCustomerOffersPage(prevPageNumber => prevPageNumber + 1)
            }
        })
        if (node) customerOffersObserver.current.observe(node)
    }, [isLoadingCustomerOffers, customerOffersPage])

    useEffect(() => {
        setRetailerOffersPage(0)
        setRequestOffersPage(0)
        setCustomerOffersPage(0)
    }, [currentView, JSON.stringify(offersFilter)])

    useEffect(() => {
        const source = axios.CancelToken.source()
        const token = source.token

        switch (currentView) {
            case OfferCurrentView.OfferRetailer:
                handleGetRetailerOffers(offersFilter, token)
                break
            case OfferCurrentView.OfferRequest:
                handleGetRequestOffers(offersFilter, token)
                break
            case OfferCurrentView.OfferCustomer:
                handleGetCustomerOffers(offersFilter, token)
                break
        }

        return () => {
            source.cancel("Nowe zapytanie")
        }
    }, [currentView, retailerOffersPage, requestOffersPage, customerOffersPage, JSON.stringify(offersFilter)])


    useEffect(() => {
        if (currentUser === "") {
            window.location.href = '/login'
        }
    }, [currentUser])

    const generateCreateOfferButton = () => {
        if (!authState.user || !authState.user.userId) {
            return null
        }
        else if ((currentView === OfferCurrentView.OfferCustomer && authState.user.isOperator && authState.selectedCustomerData) || (currentView === OfferCurrentView.OfferRetailer && authState.user && !authState.user.isOperator)) {
            let mode = authState.user.isOperator ? 0 : 1
            return <button className="btn-app-secondary" onClick={() => handleRedirectToOfferRequest(mode)}>Przygotuj ofertę dla klienta</button>
        }
        else if (currentView === OfferCurrentView.OfferRequest && authState.user && !authState.user.isOperator) {
            return <button className="btn-app-secondary" onClick={() => handleRedirectToOfferRequest(0)}>Złóż zapytanie ofertowe</button>
        }
    }

    const handleRedirectToOfferRequest = (mode) => {
        navigate(`/OfferRequest/${mode}/false/null`)
    }

    const handleCreateOfferRequest = (offerItems) => {
        if (offerItems && offerItems.length > 0) {
            setItemsToCreateOfferRequest(offerItems)
            setShowCreateOfferRequestModal(true)
        }
        else {
            toast.warning('Oferta nie zawiera produktów!')
        }
    }

    const handleEditOffer = (offerItems, offerNo) => {
        if (offerItems && offerItems.length > 0) {
            setItemsToEditOffer(offerItems)
            setOfferNo(offerNo)
            setShowEditOfferModal(true)
        }
        else {
            toast.warning('Oferta nie zawiera produktów!')
        }
    }

    const handleEditOfferModalAnswer = async (answer) => {
        if (answer) {
            setIsLoadingOfferRequest(true)
            setLoadingText('Wczytywanie oferty ...')
            await handleDeleteSavedOffer(0)
            let dateNow = new Date()
            const offerRequest = {
                investor: null, investName: '', additionalInfo: '', expectedDate: dateNow.setDate(dateNow.getDate() + 14), retailerTax: 23,
                totalPriceMarkup: 0, serviceNetValue: 0
            }
            itemsToEditOffer.forEach(i => {
                i.priceMarkup = 0
                i.costNet = i.priceNet
                i.product = {}
                i.product.title = i.externalArticle ? i.artName : ''
            })
            const futureDate = new Date('2125-10-21T10:02:01.890')
            await handleCreateUpdateSavedOffer(0, offerRequest, itemsToEditOffer, futureDate.toISOString())
            setIsLoadingOfferRequest(false)
            navigate(`/OfferRequest/0/true/${offerNo}`)
        }
        setItemsToEditOffer([])
        setOfferNo(null)
        setShowEditOfferModal(false)
    }

    const handleCreateOfferRequestModalAnswer = async (answer) => {
        if (answer) {
            await handleCreateOfferMainFunc(false)
        }
        setShowCreateOfferRequestModal(false)
        setItemsToCreateOfferRequest(null)
    }

    const handleCreateRetailerOffer = (offerItems, offerExpiredDate) => {
        if (offerItems && offerItems.length > 0) {
            setItemsToCreateRetailerOffer(offerItems)
            setOfferExpiredDate(offerExpiredDate)
            setShowCreateRetailerOfferModal(true)
        }
        else {
            toast.warning('Oferta nie zawiera produktów!')
        }
    }

    const handleCreateRetailerOfferModalAnswer = async (answer) => {
        if (answer) {
            await handleCreateOfferMainFunc(true)
        }
        setShowCreateRetailerOfferModal(false)
        setItemsToCreateRetailerOffer(null)
    }

    const handleCreateOfferMainFunc = async (isRetailerOffer) => {
        setIsLoadingOfferRequest(true)
        setLoadingText(isRetailerOffer ? 'Wczytywanie oferty...' : 'Wczytywanie zapytania ofertowego...')
        let offerMode = isRetailerOffer ? 1 : 0

        await handleDeleteSavedOffer(offerMode)
        let dateNow = new Date()
        const offerRequest = {
            investor: null, investName: '', additionalInfo: '', expectedDate: dateNow.setDate(dateNow.getDate() + 14), retailerTax: 23,
            totalPriceMarkup: 0, serviceNetValue: 0
        }
        let tmpOfferItems = isRetailerOffer ? itemsToCreateRetailerOffer : itemsToCreateOfferRequest
        tmpOfferItems.forEach(i => {
            i.priceMarkup = 0
            i.costNet = i.priceNet
            i.product = {}
            i.product.title = i.externalArticle ? i.artName : ''
        })
        await handleCreateUpdateSavedOffer(offerMode, offerRequest, tmpOfferItems, offerExpiredDate)
        setOfferExpiredDate(null)
        setIsLoadingOfferRequest(false)
        navigate(`/OfferRequest/${offerMode}/true/null`)
    }

    const handleDeleteRetailerOfferFunc = async (offerNo, offerSiteId) => {
        let result = await handleDeleteRetailerOffer(offerNo, offerSiteId)
        if (result) {
            setRetailerOffersPage(0)
        }
        else {
            toast.error('Nie udało się usunąć oferty!')
        }
    }

    const handleDownloadPdf = async (offerNo, offerSiteId, offerCustId) => {
        if (currentView === OfferCurrentView.OfferRetailer) {
            setDownloadOfferPdfModalObj({ show: true, offerNo: offerNo, offerSiteId: offerSiteId })
        }
        else {
            await downloadCustomerOffer(offerNo, offerSiteId, offerCustId)
            handleGetNumberOfUnreadCustomerOffer()
        }

        if (currentView === OfferCurrentView.OfferCustomer) {
            await handleChangeOfferStatus(offerNo, offerSiteId)
        }
    }

    const handleDownloadPdfModalAnswer = (answer) => {
        if (!answer || answer === 'Cancel') {
            setDownloadOfferPdfModalObj({ show: false, offerNo: -1, offerSiteId: -1 })
            return
        }
        let boolAnswer = answer === 'Yes' ? true : false
        handleDownloadRetailerOfferPdf(downloadOfferPdfModalObj.offerNo, downloadOfferPdfModalObj.offerSiteId, boolAnswer)
        setDownloadOfferPdfModalObj({ show: false, offerNo: -1, offerSiteId: -1 })
    }

    const generateTable = () => {
        if (currentView === OfferCurrentView.OfferCustomer) {
            return <OffersTable
                offers={customerOffers}
                isLoading={isLoadingCustomerOffers}
                currentView={currentView}
                handleCreateOfferRequest={handleCreateOfferRequest}
                handleDownloadPdf={handleDownloadPdf}
                downloadingOfferNo={downloadingOfferNo}
                setIsLoadingBasket={setIsLoadingBasket}
                lastBookElementRef={lastBookElementRefCustomerOffers}
                offersFilter={offersFilter}
                setOffersFilter={setOffersFilter}
                handleCreateRetailerOffer={handleCreateRetailerOffer}
                handleEditOffer={handleEditOffer}
            />
        }
        else if (currentView === OfferCurrentView.OfferRetailer) {
            return <OffersTable
                offers={retailerOffers}
                isLoading={isLoadingRetailerOffers}
                currentView={currentView}
                handleCreateRetailerOffer={handleCreateRetailerOffer}
                handleDeleteRetailerOfferFunc={handleDeleteRetailerOfferFunc}
                handleDownloadPdf={handleDownloadPdf}
                downloadingOfferNo={downloadingOfferNo}
                setIsLoadingBasket={setIsLoadingBasket}
                lastBookElementRef={lastBookElementRefRetailerOffers}
                offersFilter={offersFilter}
                setOffersFilter={setOffersFilter}
                handleEditOffer={handleEditOffer}
            />
        }
        else if (currentView === OfferCurrentView.OfferRequest) {
            return <OffersTable
                offers={requestOffers}
                isLoading={isLoadingRequestOffers}
                currentView={currentView}
                setIsLoadingBasket={setIsLoadingBasket}
                lastBookElementRef={lastBookElementRefRequestOffers}
                offersFilter={offersFilter}
                setOffersFilter={setOffersFilter}
                handleEditOffer={handleEditOffer}
            />
        }
        else return null
    }

    if (!isUserHasAccess(UserRole.Offers)) {
        return <NoAccess />
    }
    if (!isValidLicense) {
        return <NoAccess isLicenseError={true} />
    }
    if (isLoadingOfferRequest || isLoadingBasket) {
        return <LoadingSpinner loadingText={isLoadingBasket ? 'Wczytywanie pozycji do koszyka...' : loadingText} />
    }
    return (
        <>
            <ErrorBoundary>
                <div>
                    <YesNoMessageModal
                        show={showEditOfferModal}
                        onHide={handleEditOfferModalAnswer}
                        title={'Edycja oferty'}
                        message={'Uwaga! Jeżeli masz nie zakończoną oferte zostanie ona nadpisana! Czy chcesz kontynować?'}
                        showCloseButton={false}
                    />
                    <YesNoMessageModal
                        show={showCreateOfferRequestModal}
                        onHide={handleCreateOfferRequestModalAnswer}
                        title={'Tworzenie zapytania ofertowego'}
                        message={'Uwaga! Jeżeli masz nie zakończone zapytanie ofertowe zostanie ono nadpisane! Czy chcesz stworzyć nowe zapytanie ofertowe?'}
                        showCloseButton={false}
                    />
                    <YesNoMessageModal
                        show={showCreateRetailerOfferModal}
                        onHide={handleCreateRetailerOfferModalAnswer}
                        title={'Tworzenie oferty'}
                        message={'Uwaga! Jeżeli masz nie zakończoną oferte zostanie ona nadpisana! Czy chcesz stworzyć nową oferte?'}
                        showCloseButton={false}
                    />
                    <YesNoCancelMessageModal
                        show={downloadOfferPdfModalObj.show}
                        onHide={handleDownloadPdfModalAnswer}
                        title={'Pobieranie pliku'}
                        message={'Czy pobrać ofertę z cenami jednostkowymi?'}
                    />
                    <ScrollUpArrow />
                    <div className='container-fluid-with-paddings mt-4' style={{ marginBottom: isMobile ? '80px' : '0px' }}>
                        {isMobile && <div className="mb-2"><MobileAccountNavBar currentKey={AccountCurrentView.Oferty} /></div>}
                        <div className="main-container-holder">
                            <div className="row">
                                <div className="col">
                                    <div className="tabs mb-4">
                                        <button
                                            className={`tab-button ${currentView === OfferCurrentView.OfferCustomer ? 'active' : ''}`}
                                            onClick={() => setCurrentView(OfferCurrentView.OfferCustomer)}
                                        >
                                            Oferty TG
                                        </button>
                                        <button
                                            className={`tab-button ${currentView === OfferCurrentView.OfferRetailer ? 'active' : ''}`}
                                            onClick={() => setCurrentView(OfferCurrentView.OfferRetailer)}
                                        >
                                            Moje oferty
                                        </button>
                                        <button
                                            className={`tab-button ${currentView === OfferCurrentView.OfferRequest ? 'active' : ''}`}
                                            onClick={() => setCurrentView(OfferCurrentView.OfferRequest)}
                                        >
                                            Zapytania ofertowe
                                        </button>
                                    </div>
                                </div>
                                <div className="col d-flex justify-content-end align-items-start">
                                    {generateCreateOfferButton()}
                                </div>
                            </div>
                            {generateTable()}
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </>
    )
}

export default OffersPageIndex